import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { slugifyHeading } from '../../lib/api';
import { structureIfFlat } from '../../lib/structure';

const textOf = (children) =>
  Array.isArray(children) ? children.map(textOf).join('') : typeof children === 'string' ? children : children?.props?.children ? textOf(children.props.children) : '';

const Heading = ({ level, children }) => {
  const text = textOf(children);
  const id = slugifyHeading(text);
  if (level === 2) {
    return (
      <h2 id={id} className="paper-h2 font-celestial text-2xl sm:text-3xl text-slate-100 mt-20 mb-8 leading-snug scroll-mt-28">
        <span className="block w-10 h-px bg-amber-400/70 mb-6" />
        {children}
      </h2>
    );
  }
  if (level === 3) return <h3 id={id} className="font-celestial text-xl sm:text-2xl text-amber-100/90 mt-14 mb-5 scroll-mt-28">{children}</h3>;
  if (level === 1) return <h2 id={id} className="font-celestial text-2xl sm:text-3xl text-slate-100 mt-20 mb-8 scroll-mt-28">{children}</h2>;
  return <h4 id={id} className="font-mono-precision text-xs uppercase tracking-[0.25em] text-amber-300/80 mt-10 mb-4">{children}</h4>;
};

const Formula = ({ text }) => {
  const lines = String(text).trim().split('\n').map((l) => l.trim()).filter(Boolean);
  const vertical = lines.length > 1;
  const tokens = vertical
    ? lines.map((l) => l.replace(/^→\s*/, ''))
    : lines[0].split('→').map((t) => t.trim());
  return (
    <div className={`my-12 rounded-2xl border border-amber-400/15 bg-amber-400/[0.03] px-6 py-8 sm:px-10 flex ${vertical ? 'flex-col items-start gap-1' : 'flex-wrap items-center justify-center gap-y-3'}`} data-testid="paper-formula">
      {tokens.map((t, i) => (
        <span key={`${i}-${t}`} className={`flex items-center ${vertical ? 'gap-4' : ''}`}>
          {i > 0 && <span className={`font-mono-precision text-amber-400/80 ${vertical ? 'w-6 text-left' : 'px-3 sm:px-4'}`}>→</span>}
          {i === 0 && vertical && <span className="w-6" />}
          <span className={`font-mono-precision uppercase tracking-[0.25em] text-[12px] sm:text-[13px] ${i === tokens.length - 1 ? 'text-amber-200' : 'text-slate-200'} ${vertical ? 'py-1' : ''}`}>{t}</span>
        </span>
      ))}
    </div>
  );
};

const components = {
  h1: (p) => <Heading level={1} {...p} />,
  h2: (p) => <Heading level={2} {...p} />,
  h3: (p) => <Heading level={3} {...p} />,
  h4: (p) => <Heading level={4} {...p} />,
  p: ({ children }) => {
    const text = textOf(children);
    if (text.length < 60 && /^(by|written by)\s+\S/i.test(text)) {
      return <p className="paper-byline font-mono-precision text-[11px] uppercase tracking-[0.3em] text-amber-300/80 mb-10 not-italic [&_em]:not-italic">{children}</p>;
    }
    return <p className="text-[17px] sm:text-lg leading-[1.85] text-slate-300/90 mb-5">{children}</p>;
  },
  strong: ({ children }) => <strong className="text-slate-100 font-semibold">{children}</strong>,
  em: ({ children }) => <em className="text-amber-100/90">{children}</em>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer" className="text-cyan-300 underline decoration-cyan-300/30 underline-offset-4 hover:decoration-cyan-300">{children}</a>,
  blockquote: ({ children }) => (
    <blockquote className="my-10 pl-7 border-l border-amber-400/60 font-celestial text-xl sm:text-2xl leading-relaxed text-amber-50/90 italic [&_p]:text-inherit [&_p]:font-inherit [&_p]:leading-relaxed [&_p]:mb-4">{children}</blockquote>
  ),
  ul: ({ children }) => <ul className="my-7 space-y-3 pl-1">{children}</ul>,
  ol: ({ children }) => <ol className="my-7 space-y-3 pl-1 list-none [counter-reset:item]">{children}</ol>,
  li: ({ children, ordered }) => (
    <li className="relative pl-8 text-[17px] sm:text-lg leading-[1.8] text-slate-300/90 before:absolute before:left-0 before:top-[0.85em] before:w-3 before:h-px before:bg-amber-400/70 [&_p]:mb-2">{children}</li>
  ),
  hr: () => <div className="my-16 flex items-center justify-center gap-3"><span className="w-1 h-1 rounded-full bg-amber-400/60" /><span className="w-1 h-1 rounded-full bg-amber-400/60" /><span className="w-1 h-1 rounded-full bg-amber-400/60" /></div>,
  code: ({ inline, children, className }) =>
    inline || !className ? (
      <code className="font-mono-precision text-[0.9em] text-cyan-200 bg-white/[0.06] px-1.5 py-0.5 rounded">{children}</code>
    ) : (
      <code className={`${className} font-mono-precision text-sm`}>{children}</code>
    ),
  pre: ({ children }) => {
    const child = Array.isArray(children) ? children[0] : children;
    if (child?.props?.className?.includes('language-formula')) return <Formula text={child.props.children} />;
    return <pre className="my-8 p-6 rounded-xl border border-white/[0.08] bg-[#060a14] overflow-x-auto text-sm leading-relaxed text-slate-300">{children}</pre>;
  },
  table: ({ children }) => <div className="my-10 overflow-x-auto rounded-xl border border-white/[0.08]"><table className="w-full text-left text-sm">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-white/[0.04] font-mono-precision text-[10px] uppercase tracking-[0.2em] text-amber-300/80">{children}</thead>,
  th: ({ children }) => <th className="px-5 py-3 font-medium">{children}</th>,
  td: ({ children }) => <td className="px-5 py-3.5 border-t border-white/[0.06] text-slate-300/90 align-top">{children}</td>,
  img: ({ src, alt }) => <img src={src} alt={alt || ''} className="my-10 rounded-xl border border-white/[0.08] max-w-full" loading="lazy" />,
};

export default function PaperBody({ markdown, lead = false }) {
  return (
    <div className={`paper-prose ${lead ? 'paper-lead' : ''}`} data-testid="paper-body">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{structureIfFlat(markdown)}</ReactMarkdown>
    </div>
  );
}
